import { useRecordContext } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Contact, DoorCategory } from "../types";
import { useMarcoDoorDetail, type DoorEvidenceEntry } from "./useMarcoDoorDetail";
import { useMarcoAssessments } from "./useMarcoAssessments";
import { MarcoAssessmentSection } from "./MarcoAssessmentSection";

const DOOR_LABELS: Record<DoorCategory, string> = {
  door_0_google_signal: "Door 0 — Google signal",
  door_1_client_deficit: "Door 1 — Client deficit",
  door_2_delivery_overwhelm: "Door 2 — Delivery overwhelm",
  door_3_lack_of_control: "Door 3 — Lack of control",
};

const DOOR_KEYS: DoorCategory[] = [
  "door_1_client_deficit",
  "door_2_delivery_overwhelm",
  "door_3_lack_of_control",
];

function DoorBreakdownItem({
  doorKey,
  entry,
  isPrimary,
}: {
  doorKey: DoorCategory;
  entry: DoorEvidenceEntry | undefined;
  isPrimary: boolean;
}) {
  return (
    <AccordionItem value={doorKey}>
      <AccordionTrigger className="text-sm">
        <span className="flex items-center gap-2">
          {DOOR_LABELS[doorKey]}
          {isPrimary && (
            <Badge variant="secondary" className="text-xs font-normal">
              primary
            </Badge>
          )}
          {entry && (
            <span className="text-muted-foreground text-xs">
              strength {entry.strength}
            </span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-1 text-sm">
        {entry ? (
          <>
            <ul className="list-disc pl-4">
              {entry.pains.map((pain, i) => (
                <li key={i}>{pain}</li>
              ))}
            </ul>
            {entry.evidence && (
              <p className="text-muted-foreground text-xs italic">
                &ldquo;{entry.evidence}&rdquo;
              </p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground text-xs italic">
            No evidence recorded for this door.
          </p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function MarcoCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Marco</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </CardContent>
    </Card>
  );
}

/**
 * PRD #16 §E — always renders, populated or empty (decision #3). Reads
 * mentorax.lead_doors + mentorax.lead_scores directly via useMarcoDoorDetail,
 * not the lead_snapshots read-model (which is deliberately summary-only).
 *
 * MX-MARCO-UI-003: the assessment block (useMarcoAssessments) renders above the
 * legacy door/score content. Both queries stay independent, so a legacy error
 * does not hide an assessment, and an assessment error is not shown as "no
 * intelligence".
 */
export const MarcoCard = () => {
  const record = useRecordContext<Contact>();
  const contactId = record ? Number(record.id) : undefined;
  const { data, isPending, isError } = useMarcoDoorDetail(contactId);
  const assessments = useMarcoAssessments(contactId);

  if (isPending && assessments.isPending) return <MarcoCardSkeleton />;

  const hasAssessment =
    !!assessments.data?.approved || (assessments.data?.pending.length ?? 0) > 0;

  const compositeScore = data?.scores.find((s) => s.score_type === "composite");
  const icpScore = data?.scores.find((s) => s.score_type === "icp_fit");
  const intentScore = data?.scores.find((s) => s.score_type === "intent");
  const painTags = compositeScore?.pain_tags ?? icpScore?.pain_tags ?? [];
  const entryPoint = compositeScore?.entry_point ?? icpScore?.entry_point;
  const quality = (record as any)?.enrichment_quality as
    | Record<string, { tool_used?: string; confidence?: string }>
    | null
    | undefined;

  let legacyContent: React.ReactNode = null;
  if (isPending) {
    legacyContent = null;
  } else if (isError) {
    legacyContent = (
      <p className="text-destructive text-xs">
        Couldn't load Marco's intelligence.
      </p>
    );
  } else if (!data) {
    if (!hasAssessment && assessments.isSuccess) {
      legacyContent = (
        <p className="text-muted-foreground text-xs italic">
          No Marco intelligence yet — run a re-scan to enrich this contact.
        </p>
      );
    }
  } else {
    legacyContent = (
      <>
        {data.primary_door && (
          <div>
            <span className="font-medium">Primary: </span>
            {DOOR_LABELS[data.primary_door]}
          </div>
        )}
        {data.secondary_door && (
          <div>
            <span className="font-medium">Secondary: </span>
            {DOOR_LABELS[data.secondary_door]}
            {data.ambiguity_note && (
              <p className="text-muted-foreground text-xs mt-0.5">
                {data.ambiguity_note}
              </p>
            )}
          </div>
        )}

        <Accordion
          type="single"
          collapsible
          defaultValue={data.primary_door ?? undefined}
        >
          {DOOR_KEYS.map((key) => (
            <DoorBreakdownItem
              key={key}
              doorKey={key}
              entry={data.door_evidence?.[key]}
              isPrimary={data.primary_door === key}
            />
          ))}
        </Accordion>

        {data.rationale && (
          <div>
            <span className="font-medium">Rationale: </span>
            <span className="text-muted-foreground">{data.rationale}</span>
          </div>
        )}

        {(compositeScore || icpScore || intentScore) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {compositeScore?.current_score != null && (
              <span>Score: {compositeScore.current_score}</span>
            )}
            {icpScore?.current_score != null && (
              <span>ICP fit: {icpScore.current_score}</span>
            )}
            {intentScore?.current_score != null && (
              <span>Intent: {intentScore.current_score}</span>
            )}
            {entryPoint && <span>Entry point: {entryPoint}</span>}
          </div>
        )}

        {painTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {painTags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {quality && (
          <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            {Object.entries(quality).map(([task, info]) => (
              <span key={task}>
                {task.replace(/_/g, " ")}: {info?.confidence ?? "unavailable"}
                {info?.tool_used ? ` (${info.tool_used})` : ""}
              </span>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Marco</CardTitle>
        <div className="flex items-center gap-2">
          {(record as any)?.current_tier && (
            <Badge variant="outline">{(record as any).current_tier}</Badge>
          )}
          {data?.confidence && (
            <Badge variant="secondary" className="text-xs font-normal">
              confidence: {data.confidence}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <MarcoAssessmentSection
          data={assessments.data}
          isPending={assessments.isPending}
          isError={assessments.isError}
          error={assessments.error}
        />
        {legacyContent}
      </CardContent>
    </Card>
  );
};
